import { Form, Formik } from 'formik';
import ModalComponent from '../../../components/modal/modal';
import { getFilteredPermissionData } from '../../../utils';
import { ROLES_VALIDATION } from '../../../utils/validations/validations';
import { type ModalStatusType } from '../../../types/common';
import { useSelector } from 'react-redux';
import { selectAuthRoutesList } from '../../configuration/routeSlice';
import ModalFooterComponent from '../../../components/modal/modalFooterComponent';
import { SORTED_FIELD_PERMISSION, SORTED_MODULE_PERMISSION } from '../../../constants/common';
import { handleFilterPermissionList, handleSortPermissionNames } from '../../../utils/global';
import { useWealthlaneCustomerApi } from '../../../hooks';
import ModalBody from '../../../components/modal/modalBodyComponent';
import _ from 'lodash';
import useGetFeaturePermissionSet from '../../../hooks/get/useGetFeaturePermissionSet';
import { convertPermissions } from '../../../utils/permission/permissionAdapter';
import { useState } from 'react';
import {
  PermissionMatrixForm,
  useFilteredPermissionMatrix,
  PERMISSION_MODAL_TOOLTIP,
} from '../shared/permission-matrix';

interface FilterRolesModalType {
  isModalOpen: boolean;
  closeModal: () => void;
  modalStatus: ModalStatusType;
  setModalStatus: (value: ModalStatusType) => void;
  setRolesInitialData: () => void;
  rolesInitialData: { data: any; id: string };
  loading: boolean;
  setFilterCustomValue: (value: string) => void;
}

const FilterRolesModal = ({
  isModalOpen,
  closeModal,
  modalStatus,
  rolesInitialData,
  setModalStatus,
  setRolesInitialData,
  loading,
  setFilterCustomValue,
}: FilterRolesModalType): JSX.Element => {
  const { handleRequest } = useWealthlaneCustomerApi();
  const { data: newPermissions } = useGetFeaturePermissionSet(handleRequest);

  const userAuthList = useSelector(selectAuthRoutesList);
  const allFieldValues = userAuthList.localization?.values?.Wealthlane;

  const { nonFieldLevelPermissionsGroups, fieldLevelPermissionsGroups } = getFilteredPermissionData(
    convertPermissions(newPermissions ?? []),
    'CustomerService.Customer.',
    allFieldValues
  );
  const [manualSearch, setManualSearch] = useState('');

  const filterNonFieldLevelPermission = handleSortPermissionNames(
    handleFilterPermissionList(nonFieldLevelPermissionsGroups),
    SORTED_MODULE_PERMISSION
  );
  const filterFieldLevelPermission = handleSortPermissionNames(
    handleFilterPermissionList(fieldLevelPermissionsGroups),
    SORTED_FIELD_PERMISSION
  );

  const { filteredData, filteredFieldLevelData } = useFilteredPermissionMatrix({
    filterNonFieldLevelPermission,
    filterFieldLevelPermission,
    manualSearch,
    allFieldValues,
  });

  const handleSubmit = (values: any): void => {
    delete values.permissions['CustomerService-Customer'];
    delete values.fieldLevelPermissions['CustomerService-Customer-FieldLevel'];
    const payload = {
      ...values,
      permissions: Object.keys(values?.permissions)
        .filter((x) => values.permissions[x] !== false)
        .map((item) => item.replaceAll('-', '.')),
      fieldLevelPermissions: Object.keys(values?.fieldLevelPermissions)
        .filter((x) => values.fieldLevelPermissions[x] !== false)
        .map((item) => item.replaceAll('-', '.')),
    };

    setFilterCustomValue(payload?.permissions?.concat(payload?.fieldLevelPermissions));
    closeModal();
  };

  const handleCheckedAll = (
    key: string,
    setFieldValue: any,
    dataList: any,
    wrapperName: string,
    isCheckedAll: boolean
  ): void => {
    const allFields = handleFilterPermissionList(dataList)[key];
    const filterAllDisabledData = allFields.filter(
      (item: any) => !(item.isDisabled as boolean) && item
    );

    if (isCheckedAll) {
      allFields.forEach((item: any) =>
        setFieldValue(`${wrapperName}[${String(item.key.replaceAll('.', '-') ?? '')}]`, false)
      );
    } else {
      filterAllDisabledData.forEach((item: any) =>
        setFieldValue(`${wrapperName}[${String(item.key.replaceAll('.', '-') ?? '')}]`, true)
      );
    }
  };

  return (
    <Formik
      enableReinitialize
      initialValues={rolesInitialData.data}
      validationSchema={ROLES_VALIDATION}
      onSubmit={(values) => {
        handleSubmit(values);
      }}>
      {({ touched, setFieldValue, errors, values }) => {
        return (
          <ModalComponent
            id='roleModalContainer'
            show={isModalOpen}
            closeModal={() => {
              closeModal();
              setRolesInitialData();
            }}
            loading={loading}
            size='lg'
            valueEdited={_.isEqual(values, rolesInitialData?.data)}
            title='Filter System Role'
            showToolTip={true}
            tooltipText={PERMISSION_MODAL_TOOLTIP}
            children={
              <Form>
                <ModalBody>
                  <PermissionMatrixForm
                    variant='single'
                    modalStatus={modalStatus}
                    values={values}
                    errors={errors}
                    touched={touched}
                    setFieldValue={setFieldValue}
                    allFieldValues={allFieldValues}
                    filteredData={filteredData}
                    filteredFieldLevelData={filteredFieldLevelData}
                    nonFieldLevelPermissionsGroups={nonFieldLevelPermissionsGroups}
                    fieldLevelPermissionsGroups={fieldLevelPermissionsGroups}
                    filterNonFieldLevelPermission={filterNonFieldLevelPermission}
                    filterFieldLevelPermission={filterFieldLevelPermission}
                    manualSearch={manualSearch}
                    onSearchChange={setManualSearch}
                    searchInputId='searchRoleFilterInput'
                    handleCheckedAll={handleCheckedAll}
                  />
                </ModalBody>
                <ModalFooterComponent
                  modalStatus={modalStatus}
                  onCloseModal={() => {
                    closeModal();
                    setRolesInitialData();
                  }}
                  onClickButton={() => {
                    handleSubmit(values);
                  }}
                  buttonTitle='Filter'
                  onEditModal={() => {
                    setModalStatus('Edit');
                  }}
                  loading={false}
                  valueChanged={_.isEqual(values, rolesInitialData?.data)}
                  isEditShow={(rolesInitialData.data.isEditable as boolean) || false}
                  id='modalFooter'
                  urlName='roles'
                />
              </Form>
            }
          />
        );
      }}
    </Formik>
  );
};

export default FilterRolesModal;
